"use strict";

class StoreProducts {
  #root = null;
  #productsRoot = null;
  #allCards = [];

  #categoryToolbar = null;
  #filterContainer = null;
  #sortSelect = null;
  #searchInput = null;
  #searchToggleBtn = null;
  #searchSection = null;
  #searchSubmitBtn = null;
  #emptyState = null;

  #activeCatId = "all";
  #searchQuery = "";
  #priceRange = "all";
  #sortBy = "1";

  #addToCartForm = null;

  constructor() {
    this.#root =
      document.querySelector(".store-page") ||
      document.querySelector(".fashion-store-page") ||
      document.querySelector(".business-site-store");
    if (!this.#root) return;

    this.#productsRoot =
      this.#root.querySelector("[data-store-products-root]") ||
      this.#root.querySelector(".products-list") ||
      this.#root;
    this.#categoryToolbar = this.#root.querySelector("[data-store-category-toolbar]");
    this.#filterContainer =
      document.querySelector("[data-store-filter-container]") ||
      document.getElementById("store-aside");
    this.#sortSelect = this.#root.querySelector("#store-product-sort-by");
    this.#searchInput = this.#root.querySelector("#store-product-search-input");
    this.#searchToggleBtn = this.#root.querySelector("#btn-toggle-search");
    this.#searchSection = this.#root.querySelector("#toolbar-serach-section");
    this.#searchSubmitBtn = this.#root.querySelector("#store-product-search-submit");
    this.#emptyState = this.#root.querySelector("#store-filter-empty");

    this.#allCards = this.#productsRoot
      ? Array.from(this.#productsRoot.querySelectorAll(".product-card-col"))
      : [];

    this.#createHiddenAddToCartForm();
    this.#initViewToggle();
    this.#initSort();
    this.#initSearch();
    this.#initCategoryToolbar();
    this.#initAsideFilters();
    this.#initAddToCart();
  }

  #createHiddenAddToCartForm() {
    const form = document.createElement("form");
    form.id = "storeAddToCartForm";
    form.style.display = "none";
    form.innerHTML = `
      <input type="hidden" name="addToCartForm_id" value="" />
      <input type="hidden" name="addToCartForm_quantity" value="1" />
      <input type="hidden" name="price_type" value="" />
      <textarea name="addToCartForm_user_notes" style="display:none"></textarea>
      <button type="submit"></button>
    `;
    document.body.appendChild(form);
    this.#addToCartForm = form;
  }

  // ─── View Toggle (grid / list) ───
  #initViewToggle() {
    const buttons = this.#root.querySelectorAll(".products-list-view-type button");
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const type = btn.dataset.type;
        buttons.forEach((b) => {
          b.classList.remove("active");
          b.querySelector("i")?.classList.remove("text-primary");
        });
        btn.classList.add("active");
        btn.querySelector("i")?.classList.add("text-primary");

        this.#allCards.forEach((col) => {
          const gridView = col.querySelector(".product-card-view--grid");
          const listView = col.querySelector(".product-card-view--list");
          if (type === "list") {
            col.classList.remove("col-6", "col-md-6", "col-xl-4");
            col.classList.add("col-12");
            if (gridView) gridView.style.display = "none";
            if (listView) listView.style.display = "";
          } else {
            col.classList.remove("col-12");
            col.classList.add("col-6", "col-md-6", "col-xl-4");
            if (gridView) gridView.style.display = "";
            if (listView) listView.style.display = "none";
          }
        });

        if (this.#productsRoot) {
          this.#productsRoot.classList.remove("view-list", "view-grid");
          this.#productsRoot.classList.add(`view-${type}`);
        }
      });
    });
  }

  // ─── Sorting ───
  #initSort() {
    if (!this.#sortSelect) return;
    this.#sortSelect.addEventListener("change", () => {
      this.#sortBy = this.#sortSelect.value;
      this.#applySort();
    });
  }

  #applySort() {
    const sections = this.#productsRoot?.querySelectorAll(".product-category-section");
    if (!sections) return;

    sections.forEach((section) => {
      const row = section.querySelector(".products-category-row");
      if (!row) return;

      const cards = Array.from(row.querySelectorAll(".product-card-col"));
      cards.sort((a, b) => {
        const aPrice = parseFloat(a.dataset.price || "0");
        const bPrice = parseFloat(b.dataset.price || "0");
        const aTs = parseInt(a.dataset.createdTs || a.dataset.id || "0", 10);
        const bTs = parseInt(b.dataset.createdTs || b.dataset.id || "0", 10);

        switch (this.#sortBy) {
          case "2": return aPrice - bPrice;
          case "3": return bPrice - aPrice;
          case "4": return aTs - bTs;
          default:  return bTs - aTs;
        }
      });
      cards.forEach((c) => row.appendChild(c));
    });
  }

  // ─── Search ───
  #initSearch() {
    if (this.#searchToggleBtn && this.#searchSection) {
      this.#searchToggleBtn.addEventListener("click", () => {
        const isOpen = !this.#searchSection.classList.contains("d-none");
        this.#searchSection.classList.toggle("d-none", isOpen);
        this.#searchToggleBtn.setAttribute("aria-expanded", String(!isOpen));
        if (!isOpen && this.#searchInput) {
          this.#searchInput.focus();
        }
      });
    }

    if (this.#searchInput) {
      this.#searchInput.addEventListener("input", () => {
        this.#searchQuery = this.#searchInput.value.trim().toLowerCase();
        this.#applyFilters();
      });
    }

    if (this.#searchSubmitBtn) {
      this.#searchSubmitBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.#searchQuery = this.#searchInput?.value.trim().toLowerCase() || "";
        this.#applyFilters();
      });
    }
  }

  // ─── Category Toolbar (top chips) ───
  #initCategoryToolbar() {
    if (!this.#categoryToolbar) return;
    const chips = this.#categoryToolbar.querySelectorAll("[data-store-cat]");

    chips.forEach((chip) => {
      chip.addEventListener("click", (e) => {
        e.preventDefault();
        chips.forEach((c) => c.classList.remove("is-active"));
        chip.classList.add("is-active");
        this.#activeCatId = chip.dataset.storeCat;
        this.#syncAsideCategoryButton(this.#activeCatId);
        this.#applyFilters();
      });
    });
  }

  // ─── Aside Filters ───
  #initAsideFilters() {
    if (!this.#filterContainer) return;

    const categoryBtns = this.#filterContainer.querySelectorAll("[data-store-category-id]");
    categoryBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        categoryBtns.forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        const catId = btn.dataset.storeCategoryId;
        this.#activeCatId = catId === "0" ? "all" : catId;
        this.#syncToolbarChip(this.#activeCatId);
        this.#applyFilters();
      });
    });

    const priceRadios = this.#filterContainer.querySelectorAll("[data-store-price-input]");
    priceRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.#priceRange = radio.value;
        this.#applyFilters();
      });
    });

    const resetBtn = this.#filterContainer.querySelector("[data-store-reset-filters]");
    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        this.#activeCatId = "all";
        this.#searchQuery = "";
        this.#priceRange = "all";

        if (this.#searchInput) this.#searchInput.value = "";

        categoryBtns.forEach((b, i) => {
          b.classList.toggle("is-active", i === 0);
        });

        priceRadios.forEach((r, i) => {
          r.checked = i === 0;
        });

        this.#syncToolbarChip("all");
        this.#applyFilters();
      });
    }
  }

  #syncToolbarChip(catId) {
    if (!this.#categoryToolbar) return;
    const chips = this.#categoryToolbar.querySelectorAll("[data-store-cat]");
    chips.forEach((c) => {
      const chipCat = c.dataset.storeCat;
      c.classList.toggle("is-active", chipCat === catId || (catId === "all" && chipCat === "all"));
    });
  }

  #syncAsideCategoryButton(catId) {
    if (!this.#filterContainer) return;
    const btns = this.#filterContainer.querySelectorAll("[data-store-category-id]");
    btns.forEach((b) => {
      const btnCat = b.dataset.storeCategoryId;
      b.classList.toggle("is-active", (catId === "all" && btnCat === "0") || btnCat === catId);
    });
  }

  // ─── Unified Filtering ───
  #applyFilters() {
    let visibleCount = 0;

    this.#allCards.forEach((card) => {
      const matchesCat =
        this.#activeCatId === "all" || card.dataset.catId === this.#activeCatId;
      const matchesSearch =
        !this.#searchQuery || (card.dataset.search || "").toLowerCase().includes(this.#searchQuery);
      const matchesPrice = this.#matchPriceRange(parseFloat(card.dataset.price || "0"));

      const visible = matchesCat && matchesSearch && matchesPrice;
      card.style.display = visible ? "" : "none";
      if (visible) visibleCount++;
    });

    this.#updateSectionVisibility();
    this.#toggleEmptyState(visibleCount === 0 && this.#allCards.length > 0);
  }

  #matchPriceRange(price) {
    switch (this.#priceRange) {
      case "<25":     return price < 25;
      case "25-50":   return price >= 25 && price <= 50;
      case "50-100":  return price >= 50 && price <= 100;
      case "100-200": return price >= 100 && price <= 200;
      case ">200":    return price > 200;
      default:        return true;
    }
  }

  #updateSectionVisibility() {
    if (!this.#productsRoot) return;
    this.#productsRoot.querySelectorAll(".product-category-section").forEach((section) => {
      const hasVisible = section.querySelector('.product-card-col:not([style*="display: none"])');
      section.style.display = hasVisible ? "" : "none";
    });
  }

  #toggleEmptyState(show) {
    if (this.#emptyState) {
      this.#emptyState.classList.toggle("d-none", !show);
    }
  }

  // ─── Add to Cart ───
  #initAddToCart() {
    if (!this.#productsRoot || typeof $Cart === "undefined") return;

    this.#productsRoot.addEventListener("click", (e) => {
      const btn = e.target.closest(".add-to-cart-btn");
      if (!btn) return;
      e.preventDefault();

      const productId = btn.dataset.productId;
      const pricesCount = parseInt(btn.dataset.pricesCount || "0", 10);
      const priceId = btn.dataset.priceId || "";
      const productLink = btn.dataset.productLink || "";

      if (pricesCount > 1 && productLink) {
        window.location.href = productLink;
        return;
      }

      this.#doAddToCart(btn, productId, priceId);
    });
  }

  async #doAddToCart(triggerBtn, productId, priceId) {
    if (!this.#addToCartForm) return;

    this.#addToCartForm.querySelector('[name="addToCartForm_id"]').value = productId;
    this.#addToCartForm.querySelector('[name="addToCartForm_quantity"]').value = "1";
    this.#addToCartForm.querySelector('[name="price_type"]').value = priceId;
    this.#addToCartForm.querySelector('[name="addToCartForm_user_notes"]').value = "";

    try {
      if (typeof ShowLoading === "function") ShowLoading(triggerBtn);
      const result = await $Cart.addToCart({
        form: this.#addToCartForm,
        formId: "addToCartForm_",
        triggerElement: triggerBtn,
      });
      if (typeof Toast === "function") Toast(result);
    } catch (error) {
      if (typeof Toast === "function") Toast(error);
    } finally {
      if (typeof HideLoading === "function") HideLoading(triggerBtn);
    }
  }
}

const $storeProductsPage =
  document.querySelector(".store-page") ||
  document.querySelector(".fashion-store-page") ||
  document.querySelector(".business-site-store");
const $isDemo = $storeProductsPage?.dataset.storeDemo === "true";

if ($storeProductsPage && !$isDemo) {
  new StoreProducts();
}
